export type FilingFinancialStatementKind = 'balance-sheet' | 'cash-flow' | 'income-statement';

export interface FilingFinancialSourceLocator {
    asReportedLabel?: string | null;
    lineItemKey?: string | null;
    lineItemLabel?: string | null;
    period?: string | null;
    statementKind?: FilingFinancialStatementKind | null;
    value?: number;
}

export interface FilingFinancialSourceMatch {
    asReportedLabel: string;
    row: HTMLTableRowElement;
    valueCell: HTMLTableCellElement;
}

export interface FilingFinancialStatementSectionMatch {
    label: string;
    table: HTMLTableElement;
    target: HTMLElement;
}

interface FilingFinancialSourceCandidate extends FilingFinancialSourceMatch {
    score: number;
}

interface FilingFinancialStatementTableCandidate {
    heading: HTMLElement;
    score: number;
    table: HTMLTableElement;
}

const statementPatterns: Record<FilingFinancialStatementKind, RegExp[]> = {
    'balance-sheet': [
        /(?:(?:unaudited|audited|condensed|consolidated|combined)\s+){0,4}balance sheets?\b/i,
        /(?:(?:unaudited|audited|condensed|consolidated|combined)\s+){0,4}statements? of (?:financial position|financial condition)\b/i,
        /(?:(?:unaudited|audited|condensed|consolidated|combined)\s+){0,4}statements? of (?:assets and liabilities|net assets)\b/i,
    ],
    'cash-flow': [
        /(?:(?:unaudited|audited|condensed|consolidated|combined)\s+){0,4}statements? of cash flows?\b/i,
        /statements? of (?:(?:unaudited|audited|condensed|consolidated|combined)\s+){1,4}cash flows?\b/i,
        /(?:(?:unaudited|audited|condensed|consolidated|combined)\s+){0,4}cash flow statements?\b/i,
    ],
    'income-statement': [
        /(?:(?:unaudited|audited|condensed|consolidated|combined)\s+){0,4}statements? of (?:income|operations|earnings|loss|net income|net earnings|profit(?: or loss)?)\b/i,
        /statements? of (?:(?:unaudited|audited|condensed|consolidated|combined)\s+){1,4}(?:income|operations|earnings|loss|net income|net earnings|profit(?: or loss)?)\b/i,
        /(?:(?:unaudited|audited|condensed|consolidated|combined)\s+){0,4}income statements?\b/i,
        /(?:(?:unaudited|audited|condensed|consolidated|combined)\s+){0,4}statements? of comprehensive (?:income|loss)\b/i,
    ],
};

const statementLabels: Record<FilingFinancialStatementKind, string> = {
    'balance-sheet': 'Consolidated Balance Sheets',
    'cash-flow': 'Consolidated Statements of Cash Flows',
    'income-statement': 'Consolidated Statements of Income',
};

const semanticAliases: Record<string, string[]> = {
    earnings: ['income', 'profit'],
    income: ['earnings', 'profit'],
    liabilities: ['obligations'],
    profit: ['earnings', 'income'],
    revenue: ['revenues', 'sales'],
    revenues: ['revenue', 'sales'],
    sales: ['revenue', 'revenues'],
};

const ignoredLabelTokens = new Set(['and', 'at', 'from', 'in', 'of', 'the', 'to', 'total']);

const normalizedText = (value?: string | null) =>
    String(value ?? '')
        .replace(/\s+/g, ' ')
        .trim();

const statementContext = (table: HTMLTableElement) => {
    const precedingText: string[] = [];
    let sibling = table.previousElementSibling;

    for (let index = 0; sibling && index < 5; index += 1) {
        precedingText.unshift(normalizedText(sibling.textContent).slice(0, 400));
        sibling = sibling.previousElementSibling;
    }

    return normalizedText(
        [table.caption?.textContent, ...precedingText, normalizedText(table.textContent).slice(0, 1800)].join(' '),
    );
};

const matchingStatementLabel = (value: string, statementKind: FilingFinancialStatementKind) => {
    for (const pattern of statementPatterns[statementKind]) {
        const match = value.match(pattern)?.[0];

        if (match) {
            return normalizedText(match);
        }
    }

    return undefined;
};

const containsAnyStatementLabel = (value: string) =>
    (Object.keys(statementPatterns) as FilingFinancialStatementKind[]).some((statementKind) =>
        Boolean(matchingStatementLabel(value, statementKind)),
    );

const previousElementInDocument = (element: Element) => {
    let current: Element | null = element;

    while (current) {
        if (current.previousElementSibling) {
            current = current.previousElementSibling;

            while (current.lastElementChild) {
                current = current.lastElementChild;
            }

            return current;
        }

        current = current.parentElement;
    }

    return undefined;
};

const nearbyStatementHeading = (
    table: HTMLTableElement,
    statementKind: FilingFinancialStatementKind,
): { element: HTMLElement; score: number } | undefined => {
    if (table.caption && matchingStatementLabel(normalizedText(table.caption.textContent), statementKind)) {
        return { element: table.caption, score: 720 };
    }

    const titleRow = Array.from(table.rows)
        .slice(0, 8)
        .find((row) => matchingStatementLabel(normalizedText(row.textContent), statementKind));

    if (titleRow) {
        return { element: titleRow, score: 680 };
    }

    let precedingElement = previousElementInDocument(table);

    for (let distance = 0; precedingElement && distance < 48; distance += 1) {
        if (precedingElement instanceof HTMLElement) {
            const precedingText = normalizedText(precedingElement.textContent);

            if (
                precedingText.length <= 420 &&
                matchingStatementLabel(precedingText, statementKind)
            ) {
                return {
                    element: precedingElement,
                    score: Math.max(500, 640 - distance * 4),
                };
            }

            if (precedingText.length <= 420 && containsAnyStatementLabel(precedingText)) {
                break;
            }
        }

        precedingElement = previousElementInDocument(precedingElement);
    }

    return undefined;
};

const isInternalNavigationHeading = (heading: HTMLElement | undefined, statementKind: FilingFinancialStatementKind) =>
    Boolean(
        heading &&
        Array.from(heading.querySelectorAll('a[href]')).some((anchor) => {
            const href = anchor.getAttribute('href');

            return (
                href?.includes('#') &&
                Boolean(matchingStatementLabel(normalizedText(anchor.textContent), statementKind))
            );
        }),
    );

const financialTableEvidence = (table: HTMLTableElement) => {
    let numericCells = 0;
    let financialRows = 0;

    Array.from(table.rows).forEach((row) => {
        const cells = Array.from(row.cells);
        const rowHasLabel = cells.some((cell) => /[A-Za-z]/.test(normalizedText(cell.textContent)));
        const rowNumericCells = cells.filter((cell) => {
            const value = normalizedText(cell.textContent)
                .replace(/\[[^\]]*]/g, '')
                .replace(/[†‡*]/g, '')
                .replace(/[(),$€£¥,%\s]/g, '')
                .replace(/[−–—]/g, '-');

            return /\d/.test(value) && !/[A-Za-z]/.test(value) && Number.isFinite(Number(value.replace(/,/g, '')));
        }).length;

        numericCells += rowNumericCells;

        if (rowHasLabel && rowNumericCells) {
            financialRows += 1;
        }
    });

    return {
        financialRows,
        numericCells,
        score: Math.min(numericCells, 30) * 2 + Math.min(financialRows, 20) * 3,
    };
};

const hasInternalNavigationLink = (element: HTMLElement) =>
    Boolean(
        element.closest('a[href*="#"]') ||
        Array.from(element.querySelectorAll('a[href]')).some((anchor) => anchor.getAttribute('href')?.includes('#')),
    );

const coreFinancialStatementsPattern =
    /\bitem\s+(?:1|8)\b[\s.:\-–—]*(?:financial statements|financial information)(?:\s+and\s+supplementary data)?\b/i;
const financialStatementNotesPattern =
    /^\s*notes?\s+to\s+(?:the\s+)?(?:(?:unaudited|audited|condensed|consolidated|combined)\s+){0,4}financial statements?\b/i;

const documentSectionMarkers = (filingDocument: Document) =>
    Array.from(
        filingDocument.querySelectorAll<HTMLElement>('h1, h2, h3, h4, h5, h6, p, td, span'),
    )
        .map((element) => {
            const text = normalizedText(element.textContent);
            const kind = coreFinancialStatementsPattern.test(text)
                ? 'core'
                : financialStatementNotesPattern.test(text)
                  ? 'notes'
                  : undefined;

            if (
                !kind ||
                !text ||
                text.length > 240 ||
                hasInternalNavigationLink(element)
            ) {
                return undefined;
            }

            return { element, kind };
        })
        .filter(
            (
                marker,
            ): marker is {
                element: HTMLElement;
                kind: 'core' | 'notes';
            } => Boolean(marker),
        );

const nearestPrecedingSectionKind = (
    table: HTMLTableElement,
    markers: ReturnType<typeof documentSectionMarkers>,
) => {
    let nearestKind: 'core' | 'notes' | undefined;

    markers.forEach(({ element, kind }) => {
        if (
            element === table ||
            element.contains(table) ||
            Boolean(element.compareDocumentPosition(table) & Node.DOCUMENT_POSITION_FOLLOWING)
        ) {
            nearestKind = kind;
        }
    });

    return nearestKind;
};

const tableContainsInternalNavigation = (table: HTMLTableElement) =>
    Array.from(table.querySelectorAll('a[href]')).some((anchor) =>
        anchor.getAttribute('href')?.includes('#'),
    );

const statementPeriodScore = (table: HTMLTableElement, period?: string | null) => {
    const periodYears = normalizedText(period).match(/\b(?:19|20)\d{2}\b/g) ?? [];

    if (!periodYears.length) {
        return 0;
    }

    const tableText = normalizedText(table.textContent);

    if (periodYears.some((year) => tableText.includes(year))) {
        return 90;
    }

    return periodYears.some((year) => statementContext(table).includes(year)) ? 12 : 0;
};

const rankFinancialStatementTables = (
    filingDocument: Document,
    locator: Pick<FilingFinancialSourceLocator, 'period' | 'statementKind'>,
) => {
    if (!locator.statementKind) {
        return [];
    }

    const statementKind = locator.statementKind;
    const markers = documentSectionMarkers(filingDocument);

    return Array.from(filingDocument.querySelectorAll('table'))
        .map((table): FilingFinancialStatementTableCandidate | undefined => {
            const headingMatch = nearbyStatementHeading(table, statementKind);

            if (!headingMatch) {
                return undefined;
            }

            const evidence = financialTableEvidence(table);
            const isInternalNavigation =
                isInternalNavigationHeading(headingMatch.element, statementKind) ||
                tableContainsInternalNavigation(table);

            if (isInternalNavigation && evidence.financialRows < 3) {
                return undefined;
            }

            const sectionKind = nearestPrecedingSectionKind(table, markers);

            // Repeated figures and statement-like disclosures in the Notes are not valid
            // sources for the primary financial grids. No match is safer than a wrong link.
            if (sectionKind === 'notes') {
                return undefined;
            }

            const sectionScore = sectionKind === 'core' ? 240 : 0;
            const navigationPenalty = isInternalNavigation ? -700 : 0;

            return {
                heading: headingMatch.element,
                score:
                    headingMatch.score +
                    evidence.score +
                    statementPeriodScore(table, locator.period) +
                    sectionScore +
                    navigationPenalty,
                table,
            };
        })
        .filter((candidate): candidate is FilingFinancialStatementTableCandidate => Boolean(candidate))
        .sort((left, right) => right.score - left.score);
};

/**
 * Locates the core reported statement table for a period-column source. Unlike an
 * individual cell source, a column intentionally has no line item or numeric value.
 */
export const findFilingFinancialStatementSection = (
    filingDocument: Document,
    locator: Pick<FilingFinancialSourceLocator, 'period' | 'statementKind'>,
): FilingFinancialStatementSectionMatch | undefined => {
    if (!locator.statementKind) {
        return undefined;
    }

    const statementKind = locator.statementKind;
    const bestMatch = rankFinancialStatementTables(filingDocument, locator)[0];

    if (!bestMatch) {
        return undefined;
    }

    const target = bestMatch.heading;

    return {
        label:
            matchingStatementLabel(normalizedText(target.textContent), statementKind) ?? statementLabels[statementKind],
        table: bestMatch.table,
        target,
    };
};

const parseAccountingNumber = (value: string) => {
    const compactValue = normalizedText(value)
        .replace(/\[[^\]]*]/g, '')
        .replace(/[†‡*]/g, '')
        .replace(/\s+/g, '');

    if (!/\d/.test(compactValue) || /[A-Za-z]/.test(compactValue)) {
        return undefined;
    }

    const negative = /^\(.*\)$/.test(compactValue) || compactValue.startsWith('-') || compactValue.startsWith('−');
    const numericValue = compactValue
        .replace(/[(),$€£¥,%]/g, '')
        .replace(/[−–—]/g, '-')
        .replace(/,/g, '')
        .replace(/[^\d.+-]/g, '');
    const parsedValue = Number(numericValue);

    if (!Number.isFinite(parsedValue)) {
        return undefined;
    }

    return negative ? -Math.abs(parsedValue) : parsedValue;
};

const inlineXbrlFact = (cell: HTMLTableCellElement) => {
    return Array.from(cell.querySelectorAll('*')).find(
        (element) => element.localName.toLowerCase().split(':').pop() === 'nonfraction',
    );
};

const valuesMatch = (cell: HTMLTableCellElement, targetValue: number) => {
    const fact = inlineXbrlFact(cell);
    const displayedValue = parseAccountingNumber(fact?.textContent ?? cell.textContent ?? '');

    if (displayedValue === undefined) {
        return false;
    }

    const scale = Number(fact?.getAttribute('scale'));

    if (fact && Number.isInteger(scale)) {
        const sign = fact.getAttribute('sign') === '-' ? -1 : 1;
        const scaleMultiplier = 10 ** scale;
        const asReportedValue = Math.abs(displayedValue) * sign * scaleMultiplier;
        const tolerance = Math.max(0.51 * scaleMultiplier, Math.abs(targetValue) * 1e-9);

        return Math.abs(asReportedValue - targetValue) <= tolerance;
    }

    return [1, 1_000, 1_000_000, 1_000_000_000].some((scaleMultiplier) => {
        const scaledTarget = targetValue / scaleMultiplier;
        const tolerance = Math.max(0.51, Math.abs(scaledTarget) * 1e-7);

        return Math.abs(displayedValue - scaledTarget) <= tolerance;
    });
};

const tokenizedLabel = (value?: string | null) => {
    const withCamelCaseSpacing = String(value ?? '').replace(/([a-z\d])([A-Z])/g, '$1 $2');

    return new Set(
        withCamelCaseSpacing
            .toLowerCase()
            .replace(/[^a-z\d]+/g, ' ')
            .split(' ')
            .filter((token) => token.length > 1 && !ignoredLabelTokens.has(token)),
    );
};

const semanticLabelScore = (asReportedLabel: string, lineItemKey?: string | null, lineItemLabel?: string | null) => {
    const sourceTokens = tokenizedLabel(`${lineItemKey ?? ''} ${lineItemLabel ?? ''}`);
    const reportedTokens = tokenizedLabel(asReportedLabel);
    let matches = 0;

    sourceTokens.forEach((token) => {
        if (reportedTokens.has(token) || semanticAliases[token]?.some((alias) => reportedTokens.has(alias))) {
            matches += 1;
        }
    });

    return matches * 8;
};

const exactAsReportedLabelScore = (asReportedLabel: string, expectedLabel?: string | null) => {
    const normalizedReportedLabel = normalizedText(asReportedLabel)
        .toLowerCase()
        .replace(/[^a-z\d]+/g, ' ')
        .trim();
    const normalizedExpectedLabel = normalizedText(expectedLabel)
        .toLowerCase()
        .replace(/[^a-z\d]+/g, ' ')
        .trim();

    if (!normalizedExpectedLabel) {
        return 0;
    }

    if (normalizedReportedLabel === normalizedExpectedLabel) {
        return 180;
    }

    return normalizedReportedLabel.includes(normalizedExpectedLabel) ||
        normalizedExpectedLabel.includes(normalizedReportedLabel)
        ? 70
        : 0;
};

const extractAsReportedLabel = (row: HTMLTableRowElement, valueCell: HTMLTableCellElement) => {
    const precedingCells = Array.from(row.cells).slice(0, valueCell.cellIndex);

    for (const cell of precedingCells.reverse()) {
        const label = normalizedText(cell.textContent)
            .replace(/^[\s.:;–—-]+|[\s.:;–—-]+$/g, '')
            .replace(/\s+\([a-z\d]+\)$/i, '');

        if (label && parseAccountingNumber(label) === undefined && /[A-Za-z]/.test(label)) {
            return label;
        }
    }

    return '';
};

const logicalCellRange = (row: HTMLTableRowElement, targetCell: HTMLTableCellElement) => {
    let start = 0;

    for (const cell of Array.from(row.cells)) {
        const span = Math.max(1, Number(cell.colSpan) || 1);

        if (cell === targetCell) {
            return { end: start + span - 1, start };
        }

        start += span;
    }

    return { end: targetCell.cellIndex, start: targetCell.cellIndex };
};

const rangesOverlap = (
    left: { end: number; start: number },
    right: { end: number; start: number },
) => left.start <= right.end && right.start <= left.end;

const inlineXbrlPeriodScore = (
    table: HTMLTableElement,
    valueCell: HTMLTableCellElement,
    periodYears: string[],
) => {
    const fact = inlineXbrlFact(valueCell);
    const contextReference =
        fact?.getAttribute('contextref') ??
        fact?.getAttribute('contextRef') ??
        fact?.getAttribute('context-ref');

    if (!contextReference) {
        return 0;
    }

    // Inline XBRL context IDs are not guaranteed to be valid CSS selectors.
    // eslint-disable-next-line unicorn/prefer-query-selector
    const context = table.ownerDocument.getElementById(contextReference);
    const contextText = normalizedText(context?.textContent);

    return periodYears.some((year) => contextText.includes(year)) ? 90 : 0;
};

const periodScore = (
    table: HTMLTableElement,
    row: HTMLTableRowElement,
    valueCell: HTMLTableCellElement,
    period?: string | null,
) => {
    const periodYears = normalizedText(period).match(/\b(?:19|20)\d{2}\b/g) ?? [];

    if (!periodYears.length) {
        return 0;
    }

    const xbrlScore = inlineXbrlPeriodScore(table, valueCell, periodYears);

    if (xbrlScore) {
        return xbrlScore;
    }

    const valueCellRange = logicalCellRange(row, valueCell);
    const precedingRows = Array.from(table.rows).slice(0, row.rowIndex);
    const columnContext = normalizedText(
        precedingRows
            .slice(-12)
            .flatMap((precedingRow) =>
                Array.from(precedingRow.cells)
                    .filter((cell) => rangesOverlap(logicalCellRange(precedingRow, cell), valueCellRange))
                    .map((cell) => cell.textContent),
            )
            .join(' '),
    );

    if (periodYears.some((year) => columnContext.includes(year))) {
        return 60;
    }

    return periodYears.some((year) => statementContext(table).includes(year)) ? 10 : 0;
};

/**
 * Locates the reported table row by statement, period, and numeric value. The normalized
 * provider label is only a ranking signal; the returned label always comes from the filing.
 */
export const findFilingFinancialSource = (
    filingDocument: Document,
    locator: FilingFinancialSourceLocator,
): FilingFinancialSourceMatch | undefined => {
    if (!locator.statementKind || typeof locator.value !== 'number' || !Number.isFinite(locator.value)) {
        return undefined;
    }

    const candidates: FilingFinancialSourceCandidate[] = [];
    const rankedStatementTables = rankFinancialStatementTables(filingDocument, locator);
    const bestStatementScore = rankedStatementTables[0]?.score;
    const eligibleStatementTables = rankedStatementTables.filter(
        ({ score }) => bestStatementScore !== undefined && score >= bestStatementScore - 450,
    );

    eligibleStatementTables.forEach(({ score: statementScore, table }) => {
        Array.from(table.rows).forEach((row) => {
            Array.from(row.cells).forEach((cell) => {
                if (!valuesMatch(cell, locator.value as number)) {
                    return;
                }

                const asReportedLabel = extractAsReportedLabel(row, cell);

                if (!asReportedLabel) {
                    return;
                }

                const labelScore = semanticLabelScore(asReportedLabel, locator.lineItemKey, locator.lineItemLabel);
                const reportedLabelScore = exactAsReportedLabelScore(
                    asReportedLabel,
                    locator.asReportedLabel,
                );

                if (locator.value === 0 && !labelScore && !reportedLabelScore) {
                    return;
                }

                candidates.push({
                    asReportedLabel,
                    row,
                    score:
                        statementScore +
                        periodScore(table, row, cell, locator.period) +
                        labelScore +
                        reportedLabelScore,
                    valueCell: cell,
                });
            });
        });
    });

    const bestMatch = candidates.sort((left, right) => right.score - left.score)[0];

    if (!bestMatch) {
        return undefined;
    }

    return {
        asReportedLabel: bestMatch.asReportedLabel,
        row: bestMatch.row,
        valueCell: bestMatch.valueCell,
    };
};

export const focusFilingFinancialSource = (match: FilingFinancialSourceMatch) => {
    const filingDocument = match.row.ownerDocument;

    filingDocument.querySelectorAll('[data-financial-source-match="true"]').forEach((element) => {
        if (element instanceof HTMLElement) {
            element.style.removeProperty('background-color');
            element.style.removeProperty('box-shadow');
            delete element.dataset.financialSourceMatch;
        }
    });

    match.row.dataset.financialSourceMatch = 'true';
    match.row.style.setProperty('background-color', 'rgba(22, 119, 255, 0.12)', 'important');
    match.valueCell.dataset.financialSourceMatch = 'true';
    match.valueCell.style.setProperty('box-shadow', 'inset 0 0 0 2px #1677ff', 'important');
    match.row.scrollIntoView({ behavior: 'smooth', block: 'center' });
};

export const focusFilingFinancialStatementSection = (match: FilingFinancialStatementSectionMatch) => {
    const filingDocument = match.table.ownerDocument;

    filingDocument.querySelectorAll('[data-financial-statement-section="true"]').forEach((element) => {
        if (element instanceof HTMLElement) {
            element.style.removeProperty('scroll-margin-top');
            delete element.dataset.financialStatementSection;
        }
    });

    match.target.dataset.financialStatementSection = 'true';
    match.target.style.setProperty('scroll-margin-top', '1.5rem');
    match.target.scrollIntoView({ behavior: 'smooth', block: 'start' });
};
